import { useEffect, useMemo, useState } from "react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { usePermissions } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  LockIcon,
  MoreHorizontal,
  Percent,
  PencilIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
} from "lucide-react";
import { CreateTaxRateDialog } from "@/components/CreateTaxRateDialog";
import { EditTaxRateDialog } from "@/components/EditTaxRateDialog";
import type { TaxRateRow } from "@/hooks/useInvoiceResources";
import { PageContainer, PageHeader } from "@/components/PagePrimitives";
import { EmptyState, ErrorState, HeaderRow, LoadingState, TH, TableShell } from "@/components/PageUi";
import { InfoHint } from "@/components/InfoHint";
import { toast } from "sonner";

export interface TaxRatesPageProps {}

type SourceTab = "all" | "org" | "country";

export function TaxRatesPage(props: TaxRatesPageProps) {
  const api = useDolfinClient();
  const { can } = usePermissions();
  const canManage = can("manageTaxRates");
  const [taxRates, setTaxRates] = useState<TaxRateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<TaxRateRow | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [rateToDelete, setRateToDelete] = useState<TaxRateRow | null>(null);
  const [tab, setTab] = useState<SourceTab>("all");
  // The whole list is already on the page, so the search filters in memory and needs no debounce.
  const [search, setSearch] = useState("");

  async function fetchTaxRates() {
    setLoading(true);
    setError(null);

    const { data, error: apiError } = await api.GET("/v1/tax-rates");

    if (apiError) {
      setError("Couldn't load your tax rates.");
    } else {
      setTaxRates(((data as any)?.data as TaxRateRow[]) ?? []);
    }
    setLoading(false);
  }

  useEffect(() => {
    fetchTaxRates();
  }, []);

  async function confirmDelete() {
    const target = rateToDelete;
    if (!target) return;
    const id = target.id;
    setRateToDelete(null);
    setDeletingId(id);
    const { error: apiError } = await api.DELETE("/v1/tax-rates/{id}", {
      params: { path: { id } },
    });
    setDeletingId(null);

    if (apiError) {
      toast.error(apiErrorMessage(apiError, "Failed to delete tax rate"));
      return;
    }

    toast.success("Tax rate deleted");
    setTaxRates((prev) => prev.filter((t) => t.id !== id));
  }

  const countryCount = taxRates.filter((t) => t.source === "country").length;
  const orgCount = taxRates.length - countryCount;

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return taxRates
      .filter((t) => tab === "all" || t.source === tab)
      .filter(
        (t) =>
          !needle ||
          t.name.toLowerCase().includes(needle) ||
          t.code.toLowerCase().includes(needle) ||
          String(t.rate).includes(needle),
      )
      .sort((a, b) => (a.source === b.source ? 0 : a.source === "org" ? -1 : 1));
  }, [taxRates, tab, search]);

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Receivables"
        title="Tax rates"
        subtitle={
          !loading
            ? `${countryCount} country default${countryCount === 1 ? "" : "s"} · ${orgCount} custom`
            : undefined
        }
        actions={
          <Button onClick={() => setCreateOpen(true)} className="w-full gap-1.5 sm:w-auto">
            <PlusIcon className="size-4" />
            New rate
          </Button>
        }
      />

      {loading ? (
        <LoadingState label="Loading tax rates..." />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchTaxRates} />
      ) : taxRates.length === 0 ? (
        <EmptyState
          icon={Percent}
          title="No tax rates yet"
          action={
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              <PlusIcon className="size-4" /> New rate
            </Button>
          }
        >
          Add the rates you charge on invoices. Your country's standard rates appear here once your
          organisation's country is set.
        </EmptyState>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <Tabs value={tab} onValueChange={(v) => setTab(v as SourceTab)}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="org">
                  Custom <span className="ml-1 text-muted-foreground tabular-nums">{orgCount}</span>
                </TabsTrigger>
                <TabsTrigger value="country">
                  Platform defaults{" "}
                  <span className="ml-1 text-muted-foreground tabular-nums">{countryCount}</span>
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <div className="relative w-full sm:w-64">
              <SearchIcon className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, code or rate"
                className="h-9 pl-8"
                aria-label="Search tax rates"
              />
            </div>
          </div>

          {visible.length === 0 ? (
            <EmptyState icon={SearchIcon} title="No matching rates">
              {search ? `Nothing matches "${search.trim()}".` : "There are no rates of this kind yet."}
            </EmptyState>
          ) : (
            <TableShell>
              <Table>
                <TableHeader>
                  <HeaderRow>
                    <TableHead className={TH}>Name</TableHead>
                    <TableHead className={`w-[100px] ${TH}`}>Code</TableHead>
                    <TableHead className={`w-[100px] text-right ${TH}`}>Rate</TableHead>
                    <TableHead className={TH}>Source</TableHead>
                    <TableHead className="w-[48px]" />
                  </HeaderRow>
                </TableHeader>
                <TableBody>
                  {visible.map((t) => {
                    const isDeleting = deletingId === t.id;
                    return (
                      <TableRow key={t.id}>
                        <TableCell className="font-semibold">{t.name}</TableCell>
                        <TableCell>
                          <span className="rounded border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
                            {t.code}
                          </span>
                        </TableCell>
                        <TableCell className="text-right font-bold tabular-nums">{t.rate}%</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {t.source === "country" ? (
                            <span className="inline-flex items-center gap-1.5">
                              <LockIcon className="size-3.5" aria-hidden />
                              Platform default
                              <InfoHint label="Platform default">
                                Standard rates for your country, kept up to date by Dolfin. They can be used on
                                invoices but not edited or deleted.
                              </InfoHint>
                            </span>
                          ) : (
                            "Custom rate"
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {!t.isReadOnly && canManage ? (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  disabled={isDeleting}
                                  className="size-8"
                                  aria-label={`Actions for ${t.name}`}
                                >
                                  {isDeleting ? (
                                    <Spinner className="size-4" />
                                  ) : (
                                    <MoreHorizontal className="size-4" />
                                  )}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-40">
                                <DropdownMenuItem onClick={() => setEditing(t)}>
                                  <PencilIcon className="mr-2 size-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setRateToDelete(t)}
                                >
                                  <TrashIcon className="mr-2 size-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          ) : null}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableShell>
          )}
        </>
      )}

      <CreateTaxRateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={fetchTaxRates}
      />

      <EditTaxRateDialog
        taxRate={editing}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
        onUpdated={fetchTaxRates}
      />

      <AlertDialog
        open={rateToDelete !== null}
        onOpenChange={(open) => !open && setRateToDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete tax rate?</AlertDialogTitle>
            <AlertDialogDescription>
              {rateToDelete?.name} ({rateToDelete?.code}) will be permanently removed from your organisation.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction variant="destructive" onClick={confirmDelete}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
